import { getAnalytics, logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { app } from '../constants/firebaseConfig';

// Initialize Analytics
let analytics = null;

try {
    analytics = getAnalytics(app);
    console.log('📊 Firebase Analytics initialized');
} catch (error) {
    console.error('❌ Error initializing Analytics:', error);
}

/**
 * Log a custom event to Firebase Analytics
 * @param {string} eventName - Name of the event
 * @param {Object} eventParams - Event parameters
 */
export const logAnalyticsEvent = (eventName, eventParams = {}) => {
    if (!analytics) {
        console.warn('⚠️ Analytics not initialized');
        return;
    }

    try {
        logEvent(analytics, eventName, eventParams);
        console.log(`📊 Event logged: ${eventName}`, eventParams);
    } catch (error) {
        console.error(`❌ Error logging event ${eventName}:`, error);
    }
};

/**
 * Log page view event
 * @param {string} pageName - Name of the page
 * @param {string} pageTitle - Title of the page
 */
export const logPageView = (pageName, pageTitle) => {
    logAnalyticsEvent('page_view', {
        page_name: pageName,
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: window.location.pathname
    });
};

/**
 * Log image upload event
 * @param {string} uploadMethod - Method used (file, camera, paste, drag)
 * @param {number} fileSize - Size of the image in bytes
 */
export const logImageUpload = (uploadMethod, fileSize) => {
    logAnalyticsEvent('image_upload', {
        upload_method: uploadMethod,
        file_size: fileSize,
        timestamp: new Date().toISOString()
    });
};

/**
 * Log solution request event
 */
export const logSolutionRequest = () => {
    logAnalyticsEvent('solution_request', {
        timestamp: new Date().toISOString()
    });
};

/**
 * Log solution success event
 * @param {number} responseTime - Time taken to get solution in ms
 */
export const logSolutionSuccess = (responseTime) => {
    logAnalyticsEvent('solution_success', {
        response_time_ms: responseTime,
        timestamp: new Date().toISOString()
    });
};

/**
 * Log solution error event
 * @param {string} errorMessage - Error message
 * @param {string} errorType - Type of error
 */
export const logSolutionError = (errorMessage, errorType) => {
    logAnalyticsEvent('solution_error', {
        error_message: errorMessage,
        error_type: errorType,
        timestamp: new Date().toISOString()
    });
};

/**
 * Log PDF download event
 */
export const logPDFDownload = () => {
    logAnalyticsEvent('pdf_download', {
        timestamp: new Date().toISOString()
    });
};

/**
 * Log navigation click event
 * @param {string} linkName - Name of the link clicked
 * @param {string} destination - Destination URL
 */
export const logNavigationClick = (linkName, destination) => {
    logAnalyticsEvent('navigation_click', {
        link_name: linkName,
        destination: destination,
        timestamp: new Date().toISOString()
    });
};

/**
 * Log error event
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 */
export const logError = (error, context) => {
    logAnalyticsEvent('app_error', {
        error_message: error.message,
        error_stack: error.stack,
        context: context,
        timestamp: new Date().toISOString()
    });
};

/**
 * Log app crash event
 * @param {Error} error - Error object
 * @param {Object} errorInfo - Additional error info
 */
export const logAppCrash = (error, errorInfo) => {
    logAnalyticsEvent('app_crash', {
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo?.componentStack || 'Unknown',
        timestamp: new Date().toISOString()
    });
};

/**
 * Set user properties
 * @param {Object} properties - User properties
 */
export const setAnalyticsUserProperties = (properties) => {
    if (!analytics) {
        console.warn('⚠️ Analytics not initialized');
        return;
    }

    try {
        setUserProperties(analytics, properties);
        console.log('📊 User properties set:', properties);
    } catch (error) {
        console.error('❌ Error setting user properties:', error);
    }
};

/**
 * Set user ID
 * @param {string} userId - User ID
 */
export const setAnalyticsUserId = (userId) => {
    if (!analytics) {
        console.warn('⚠️ Analytics not initialized');
        return;
    }

    try {
        setUserId(analytics, userId);
        console.log('📊 User ID set:', userId);
    } catch (error) {
        console.error('❌ Error setting user ID:', error);
    }
};

export default analytics;
